import { Sidebar, SidebarBody } from '@repo/ui/aceternity/sidebar'
import { StickyBanner } from '@repo/ui/aceternity/sticky-banner'
import { QueryBreadcrumbs } from '@repo/ui/commons/query-breadcrumbs'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import type { ReactNode } from 'react'
import logoBrush from '@/assets/logo-brush.png'
import logoMark from '@/assets/logo-mark.png'
import logoWordmark from '@/assets/logo-wordmark.png'
import { staleDays } from '@/lib/data-freshness'
import { useTranslation } from '@/lib/i18n'
import { systemStatusQueryOptions } from '@/routes/(private)/documents/-lib/query-options'
import { AdminNavGroups } from './admin-nav-groups'
import { AdminNavUser } from './admin-nav-user'

// The app shell: a manually collapsible icon rail that shares the available
// width with the page, shown on a rounded sheet beside it.
function AdminSidebar(props: { children: ReactNode }) {
	// Top-level pages show their own in-page <h1>; the header breadcrumb is
	// reserved for deeper pages (a detail below a list, a profile section).
	const showBreadcrumb = useLocation({
		select: (l) => l.pathname.split('/').filter(Boolean).length >= 2
	})

	return (
		<div className='flex h-dvh flex-col overflow-hidden bg-sidebar md:flex-row'>
			<Sidebar>
				<SidebarBody className='gap-6'>
					<SidebarLogo />
					<nav className='flex flex-1 flex-col'>
						<AdminNavGroups />
					</nav>
					<AdminNavUser />
				</SidebarBody>
			</Sidebar>
			<main className='flex min-h-0 min-w-0 flex-1 flex-col p-2 pt-0 md:pt-2'>
				<div className='flex h-full min-h-0 flex-col overflow-hidden rounded-xl bg-background shadow-[0_0_0_1px_var(--sidebar-border),0_1px_3px_oklch(0.3_0.02_55/0.06)]'>
					<StaleDataBanner />
					{showBreadcrumb ? (
						<header className='flex h-11 shrink-0 items-center border-border border-b px-4'>
							<QueryBreadcrumbs />
						</header>
					) : null}
					<div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
						{props.children}
					</div>
				</div>
			</main>
		</div>
	)
}

// Stale data is announced on every page, not only on the document list: an expert
// deep in a document must know the quantities rest on old sales (zero silenzi).
// The age is the dataset's, the one «Genera» reads: a sync still in flight
// does not make it younger. Dismissed, it stays away until the app reloads.
function StaleDataBanner() {
	const { t } = useTranslation()
	const status = useQuery(systemStatusQueryOptions())
	const days = staleDays(status.data?.dataset.asOf ?? null)

	if (days === null) {
		return null
	}

	return (
		<StickyBanner
			className='border-amber-300/60 border-b bg-amber-50 text-amber-900'
			closeLabel={t('po:dashboard.stale_close')}
		>
			<AlertTriangle className='size-4 shrink-0' />
			<span>{t('po:dashboard.stale_warning', { days })}</span>
		</StickyBanner>
	)
}

// The brush mark alone on the rail, the full logo once the panel opens. Both
// keep their place and cross-fade, so the nav below never moves.
function SidebarLogo() {
	return (
		<div className='relative h-16 shrink-0'>
			<img
				alt=''
				className='absolute top-1 left-1.5 size-8 transition-opacity duration-150 group-data-[state=open]/sidebar:opacity-0 motion-reduce:transition-none'
				height={32}
				src={logoMark}
				width={32}
			/>
			<div className='absolute top-0 left-1.5 w-24 opacity-0 transition-opacity duration-150 group-data-[state=open]/sidebar:opacity-100 motion-reduce:transition-none'>
				<img
					alt='Mantegna'
					className='h-auto w-full'
					height={646}
					src={logoBrush}
					width={987}
				/>
				<img
					alt=''
					className='absolute inset-0 h-auto w-full'
					height={646}
					src={logoWordmark}
					width={987}
				/>
			</div>
		</div>
	)
}

export { AdminSidebar }
